// Added by Sejune Oh on 2019-05-15
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { RefObject, FC } from 'react'
import { WithStyles, createStyles, withStyles, Theme, Fade } from '@material-ui/core'
import classNames from 'classnames'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Intl
import Intl from '@/helper/Intl'
import { MgmtValidator, ReactEmptyEvent } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Component
import Input from '@/components/common/Input'
import { Button, SnackbarContent, Snackbar } from '@/components/_primes'
import MgmtSubConfigFormStyle from '@/styles/management/MgmtSubConfigFormStyle'
import { SNACK_BAR_VALID_TIME } from '@/styles/Theme'
import Dialog from '@/components/common/Dialog'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface ResetUserPasswordFCProps extends WithStyles<typeof styles>, MgmtValidator {
    curPwdRef: RefObject<any>
    newPwdRef: RefObject<any>
    newConfirmPwdRef: RefObject<any>
    emailRef: RefObject<any>
    usernameRef: RefObject<any>
    userName: string
    userEmail: string
    onConfirmBtnClick: ReactEmptyEvent
    onCancelBtnClick: ReactEmptyEvent
    formId: string
    isErrorOccured: boolean
    onSnackbarClose: ReactEmptyEvent
    errMessage: string
    dialogRef: RefObject<any>
}

const ResetUserPassword: FC<ResetUserPasswordFCProps> = ({ validator, blurValidator, classes, ...props }) => {
    const title = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpassword.title_reset_user_password' })
    const labelUserName = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpassword.label_user_name' })
    const labelEmail = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpassword.label_email' })
    const labelCurPassword = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpassword.label_cur_password' })
    const labelConfirmPassword = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpassword.label_confirm_password' })
    const labelNewPassword = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpassword.label_new_password' })
    const btnCancelText = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpassword.btn_cancel' })
    const btnUpdateText = Intl.getIntlText({ id: 'htmljs_react.mgmt.resetuserpassword.btn_update' })

    return (
        <form id={props.formId} className={classNames(classes.Wrapper, classes.ResetUserPasswordWrapper)}>
            <div className={classes.HeaderWrapper}>{title}</div>
            <div className={classes.InputWrapper}>
                <Input
                    id={'username'}
                    type='string'
                    label={labelUserName}
                    isFullWidth={true}
                    required={true}
                    value={props.userName}
                    ref={props.usernameRef}
                    disabled
                />
                <Input
                    ref={props.emailRef}
                    id={'email'}
                    type='string'
                    label={labelEmail}
                    isFullWidth={true}
                    required={true}
                    value={props.userEmail}
                    disabled
                />
                <Input
                    ref={props.curPwdRef}
                    id={'current_pwd'}
                    label={labelCurPassword}
                    isFullWidth={true}
                    required={true}
                    isPassword={true}
                    validator={validator!.curPwdValidator}
                    autoFocus={true}
                    data-cy-input='current_pwd'
                />
                <Input
                    ref={props.newPwdRef}
                    id={'password'}
                    label={labelNewPassword}
                    isFullWidth={true}
                    required={true}
                    isPassword={true}
                    validator={validator!.pwdValidator}
                    blurValidator={blurValidator!.pwdBlurValidator}
                    data-cy-input='pwd'
                />
                <Input
                    ref={props.newConfirmPwdRef}
                    id={'confirm_pwd'}
                    label={labelConfirmPassword}
                    isFullWidth={true}
                    required={true}
                    isPassword={true}
                    validator={validator!.confirmPwdValidator}
                    blurValidator={blurValidator!.confirmPwdBlurValidator}
                    data-cy-input='confirm_pwd'
                />
            </div>
            <div className={classes.BtnWrapper}>
                <Button
                    btnText={btnUpdateText}
                    buttonType={'task'}
                    onClick={props.onConfirmBtnClick}
                    data-cy-btn='resetTask'
                />
                <Button btnText={btnCancelText} onClick={props.onCancelBtnClick} />
            </div>
            <Dialog ref={props.dialogRef} />
            {/** Snack bar for Login Status */}
            <Snackbar
                className={classes.Snackbar}
                open={props.isErrorOccured}
                onClose={props.onSnackbarClose}
                TransitionComponent={Fade}
                autoHideDuration={SNACK_BAR_VALID_TIME}
            >
                <SnackbarContent
                    className={classes.SnackbarContent}
                    evtType='error'
                    message={props.errMessage}
                    onClose={props.onSnackbarClose}
                />
            </Snackbar>
        </form>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const styles = (theme: Theme) =>
    createStyles({
        ...MgmtSubConfigFormStyle(theme),
        ResetUserPasswordWrapper: {
            minWidth: '620px',
        },
    })

export default withStyles(styles, { withTheme: true })(ResetUserPassword)
